/**
 * Plain sentences for the `meta.warnings` codes a 201 from POST /leads/intake
 * can carry (KTD9). Each code is a stable per-step identifier from
 * LeadIntakeController's post-commit steps; the patient and plan are already
 * saved when any of them appears, so every sentence says what was saved, what
 * did not happen and who to tell. Never shows the code itself.
 */

/** Title of the toast that carries these sentences. */
export const WARNING_TOAST_TITLE = 'Saved, but not everything finished';

/** Unknown or future codes. */
export const UNKNOWN_WARNING_MESSAGE =
  "The patient was saved, but a follow-up step didn't finish. Tell your Prospera administrator.";

export const WARNING_MESSAGES: Readonly<Record<string, string>> = {
  // Shared-patient link or enrolment threw.
  master_link_failed:
    "The patient was saved, but wasn't linked to the shared patient. Tell your Prospera administrator.",
  // The link was refused (data.masterLink says why). NewLeadPage already
  // explains that status in its own toast, so this sentence is only used when
  // no status came back.
  master_link_skipped:
    "The patient was saved, but wasn't linked to the shared patient. Tell your Prospera administrator.",
  // Propagating the updated details to the patient's other locations threw.
  cascade_failed:
    "The patient was saved, but the updated details weren't copied to the patient's other locations. Tell your Prospera administrator.",
  // Activity history entry or last-contact stamp threw.
  activity_log_failed:
    "The patient was saved, but it wasn't added to the activity history. Tell your Prospera administrator.",
  // Lead-created marketing event threw.
  analytics_failed:
    "The patient was saved, but it wasn't counted in marketing reports. Tell your Prospera administrator.",
  tasks_failed:
    "The patient was saved, but follow-up tasks weren't created. Ask your Prospera administrator to check the task rules.",
  // Plans list index refresh threw.
  plan_list_failed:
    "The patient was saved, but the new plan may not appear in the plans list. Tell your Prospera administrator.",
};

/** The sentence for one code; the fallback for anything unknown. */
export function warningMessage(code: string): string {
  return Object.prototype.hasOwnProperty.call(WARNING_MESSAGES, code) ? WARNING_MESSAGES[code] : UNKNOWN_WARNING_MESSAGE;
}

/**
 * Sentences for a save's warnings, de-duplicated and in arrival order.
 * `masterLinkReported` drops master_link_skipped when the page already showed
 * its own message for the link status, so staff are not told twice.
 */
export function warningMessages(codes: readonly unknown[], options: { masterLinkReported?: boolean } = {}): string[] {
  const out: string[] = [];
  for (const raw of codes) {
    const code = typeof raw === 'string' ? raw : '';
    if (code === 'master_link_skipped' && options.masterLinkReported) continue;
    const sentence = warningMessage(code);
    if (out.indexOf(sentence) === -1) out.push(sentence);
  }
  return out;
}
